import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { format, parseISO, addMonths, subDays } from 'date-fns';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { SideSheet } from '@app/app/_components/SideSheet/SideSheet';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Button } from '@app/app/_components/Button/Button';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { CheckThinIcon } from '@app/app/_icons/CheckThinIcon';
import { InsuranceStartDateStep } from '../_sections/steps/InsuranceStartDateStep';
import { TransportPolicyValidity } from '@app/domain/model/eshop/transport/TransportPolicyValidity';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';

interface Props {
    insuranceDuration?: string;
    policyValidity: Nullable<TransportPolicyValidity>;
    insurancePackage: string;
    cascoStartDate: string;
    mtplStartDate: string;
    setCascoStartDate: (startDate: string) => void;
    setMtplStartDate: (startDate: string) => void;
}

export function InsuranceStartDateSummaryPanel({
    insuranceDuration,
    policyValidity,
    insurancePackage,
    cascoStartDate,
    mtplStartDate,
    setCascoStartDate,
    setMtplStartDate,
}: Props) {
    const t = useTranslations();
    const [sideSheetOpen, setSideSheetOpen] = useState(false);

    const [tempCascoStartDate, setTempCascoStartDate] = useState(cascoStartDate);
    const [tempMtplStartDate, setTempMtplStartDate] = useState(mtplStartDate);

    const hasCasco = insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl;
    const hasMtpl = insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl;

    const calculateEndDate = useCallback(
        (startDate: string): string => {
            if (!startDate || !insuranceDuration) return '';

            const date = parseISO(startDate);
            if (isNaN(date.getTime())) return '';

            const durationInMonths = parseInt(insuranceDuration, 10);
            if (isNaN(durationInMonths)) return '';

            const endDate = subDays(addMonths(date, durationInMonths), 1);
            return format(endDate, 'yyyy-MM-dd');
        },
        [insuranceDuration],
    );

    const formatDisplayDate = (dateString: string): string => {
        if (!dateString) return '';
        try {
            return format(parseISO(dateString), 'yyyy-MM-dd');
        } catch {
            return format(dateString, 'yyyy-MM-dd');
        }
    };

    const handleOpenSideSheet = useCallback(() => {
        setTempCascoStartDate(cascoStartDate);
        setTempMtplStartDate(mtplStartDate);
        setSideSheetOpen(true);
    }, [cascoStartDate, mtplStartDate]);

    const handleCloseSideSheet = useCallback(() => {
        setSideSheetOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        setTempCascoStartDate(cascoStartDate);
        setTempMtplStartDate(mtplStartDate);
        handleCloseSideSheet();
    }, [cascoStartDate, mtplStartDate, handleCloseSideSheet]);

    const handleSave = useCallback(() => {
        setCascoStartDate(tempCascoStartDate);
        setMtplStartDate(tempMtplStartDate);
        handleCloseSideSheet();
    }, [tempCascoStartDate, tempMtplStartDate, setCascoStartDate, setMtplStartDate, handleCloseSideSheet]);

    const sideSheetHeader = (
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-300 px-6 py-4">
            <div className="flex items-center gap-3">
                <button className="cursor-pointer" onClick={handleCloseSideSheet}>
                    <ArrowLeftIcon className="h-5 w-5 text-neutral-500" />
                </button>
                <Heading level="h3">{t('eshop.transport.editCoveragePeriod')}</Heading>
            </div>
        </div>
    );

    const sideSheetFooter = (
        <div className="flex h-min w-full items-center justify-between gap-6 bg-white px-6 py-4 shadow-2xl">
            <Button
                variant="outlined"
                className="w-full"
                rightAddon={<CloseIcon className="size-4" />}
                onClick={handleCancel}>
                {t('general.cancel')}
            </Button>
            <Button className="w-full" rightAddon={<CheckThinIcon className="size-5" />} onClick={handleSave}>
                {t('general.save')}
            </Button>
        </div>
    );

    return (
        <>
            <CollapsablePanel
                label={t('eshop.transport.coveragePeriod')}
                collapsable={false}
                addon={<TextButton onClick={handleOpenSideSheet}>{t('general.edit')}</TextButton>}>
                <div className="px-6 py-2">
                    {hasCasco && (
                        <div className="mb-4 border-b border-neutral-300 pb-4">
                            <Heading level="h4" className="mb-3">
                                {t('eshop.transport.policyType.casco')}
                            </Heading>
                            <div className="grid grid-cols-2 gap-4 max-xs:grid-cols-1">
                                <ReadOnlyField label={t('eshop.transport.insuranceStartDateLabel')}>
                                    {formatDisplayDate(cascoStartDate)}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('eshop.transport.endDateLabel')}>
                                    {calculateEndDate(cascoStartDate)}
                                </ReadOnlyField>
                            </div>
                        </div>
                    )}

                    {hasMtpl && (
                        <div>
                            <Heading level="h4" className="mb-3">
                                {t('eshop.transport.policyType.mtpl')}
                            </Heading>
                            <div className="grid grid-cols-2 gap-4 max-xs:grid-cols-1">
                                <ReadOnlyField label={t('eshop.transport.insuranceStartDateLabel')}>
                                    {formatDisplayDate(mtplStartDate)}
                                </ReadOnlyField>
                                <ReadOnlyField label={t('eshop.transport.endDateLabel')}>
                                    {calculateEndDate(mtplStartDate)}
                                </ReadOnlyField>
                            </div>
                        </div>
                    )}
                </div>
            </CollapsablePanel>

            <SideSheet open={sideSheetOpen} header={sideSheetHeader} footer={sideSheetFooter}>
                {sideSheetOpen && (
                    <InsuranceStartDateStep
                        insuranceDuration={insuranceDuration}
                        policyValidity={policyValidity}
                        insurancePackage={insurancePackage}
                        setCascoStartDate={setTempCascoStartDate}
                        setMtplStartDate={setTempMtplStartDate}
                        cascoStartDate={tempCascoStartDate}
                        mtplStartDate={tempMtplStartDate}
                    />
                )}
            </SideSheet>
        </>
    );
}
